import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

const instance = axios.create({
  baseURL: `${process.env.INSIGHT_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export enum TypeComponentTemplate {
  short = 'shortAnswer',
  paragraph = 'paragraph',
  linearScale = 'linearScale',
  severalOptions = 'severalOptions'
}

export enum TypeItemsCostum {
  itemNumerText = 1,
  itemText = 'SingleLine',
  itemNumerSelect = 5,
  itemSelect = 'SelectionList',
  itemNumerNumeric = 3,
  itemNumeric = 'Numeric',
  itemNumerDate = 4,
  itemDate = 'DateTime',
  itemNumerTextArea = 2,
  itemTextArea = 'MultiLine'
}
export interface IQuestionsTemplate {
  label: string;
  isRequired?: boolean;
  orderItem: number;
  options?: string;
  typeComponent?: TypeComponentTemplate;
  maxLenght?: number;
  idSurveyQuestion?: number;
  enabled?: boolean;
}

export interface ISurveyTemplate {
  systemEntityId?: number;
  clientId?: number;
  title: string;
  description?: string;
  idTemplate?: number;
  enabled?: boolean;
  subCategoryId?: number;
  questionsTemplate: IQuestionsTemplate[];
}

export interface AnswersValues {
  value: string;
  idSurveyQuestion: number;
}
export interface SaveanswerSurvey {
  idTicket?: number;
  changeId?: number;
  problemId?: number;
  surveyTemplateId?: number;
  answersValues: AnswersValues[];
}

export interface ParamsSurvey {
  OnlyEnabled?: boolean;
  PageNumber?: number;
  descending?: boolean;
  OrderColumn?: string;
  PageSize?: number;
  text?: string;
  IsExport?: boolean;
  IdSurveyTemplate?: number;
  SystemEntityIds?: number[];
}

export interface SurveyTemplatesResponse {
  idSurveyTemplate?: number;
  title?: string;
  description?: string;
  enabled?: boolean;
  systemEntityId?: number;
  systemEntityName?: string;
  clientId?: number;
  clientName?: string;
  surveyQuestionsTemplate?: SurveyQuestionsTemplate[];
}

export interface SurveyQuestionsTemplate {
  idSurveyQuestion?: number;
  label?: string;
  isRequired?: boolean;
  orderItem?: number;
  options?: string;
  typeComponent?: string;
  maxLenght?: number;
  enabled?: boolean;
}

export interface ISurveyReport {
  idSurvey: number;
  name: string;
  createdDate: Date | string;
  status: boolean | string;
}

export interface ISurveyAsnwered {
  idSurvey: number;
  user: string;
  titleSurvey: string;
  descriptionSurvey: string;
  dateResponse: Date | string;
}

export interface ItemReport {
  type: string;
  question: string;
  answers: {
    name: string;
    value: string | number;
  }[];
}
export interface ISurveyDetail {
  items: ItemReport[];
}

export interface ISurveyAnsweredDetail {
  valueResponse: string | number;
  questions: IQuestionsTemplate[];
}
export interface KPI {
  id?: number;
  name: string;
  category: string;
  description: string;
  formula: string;
  groupId: number;
  cycle: string;
  startDate: string;
  endDate: string;
  parameterList: ParameterList[];
  stateList: StateList[];
  enabled?: boolean;
}

export interface ParameterList {
  parameterId: number;
  parameterName?: string;
  filter: string;
  code: string;
  filterData: [
    {
      id: number;
      name: string;
    }
  ];
}

export interface StateList {
  name: string;
  hexColor: string;
  minLimit: number;
  maxLimit: number;
}

export interface KPIGroup {
  id: number;
  name: string;
  enabled: boolean;
}

export interface KPIGeneralSearch {
  id: number;
  name: string;
  description: string;
  category: string;
  groupName: string;
  cycle: string;
  enabled: boolean;
  pageNumber: number;
  pageSize: number;
  orderColumn: string;
  descending: boolean;
  text: string;
  isExport: boolean;
}

export interface KPIFilter {
  parameterFilterId: number;
  filter: string;
}

export interface KPIParameter {
  id: number;
  name: string;
  description: string;
  parameterFilterId: number;
  enabled: boolean;
}

export interface KPIResult {
  cycle: number;
  result: number;
  cycleName: string;
  id?: string;
}

export interface SurveyTemplatesBychange {
  idSurveyTemplate?: number;
  title?: string;
  description?: string;
  enabled?: boolean;
  systemEntityId?: number;
  systemEntityName?: null;
  clientId?: null;
  clientName?: null;
  surveyQuestionsTemplate?: SurveyQuestionsTemplate[];
}

export interface CalculateChangePriority {
  totalScore?: number;
  calculatedPriorityId?: number;
  calculatedPriorityName?: string;
  calculatedPriorityDescription?: string;
  urgencyId?: number;
  urgencyName?: string;
  impactId?: number;
  impactName?: string;
  surveyRiskPercentage?: number;
}

export default {
  instance,
  kpi: {
    /**
     * Agrega un kpi
     *
     * @tags KPI
     * @name add
     * @request POST:/api/v1/KPI
     * @secure
     */
    add: (data: KPI, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/KPI`,
        method: 'POST',
        data,
        ...config
      }),

    update: (data: KPI, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/KPI`,
        method: 'PUT',
        data,
        ...config
      }),

    /*
     * Elimina un KPI
     *
     * @tags KPI
     * @name delete
     * @request DELETE:/api/v1/KPI/{id}
     * @secure
     * */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/KPI/${id}`,
        method: 'DELETE',
        ...config
      }),

    /**
     * Búsqueda de KPIS
     *
     * @tags KPI
     * @name generalSearch
     * @request GET:/api/v1/KPI/GeneralSearch
     * @secure
     */
    generalSearch: (
      params: KPIGeneralSearch,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<KPI>>>({
        url: `/KPI/GeneralSearch`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Evalúa un KPI
     *
     * @tags KPI
     * @name evaluate
     * @request POST:/api/v1/KPI/GetResults/{id}
     * @secure
     */
    getResults: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<KPIResult[]>>({
        url: `/KPI/GetResults/${id}`,
        method: 'GET',
        ...config
      })
  },
  kpiGroup: {
    /**
     * No description
     *
     * @tags KPIGroup
     * @name kpiGroup
     * @request GET:/api/v1/KPIGroup
     * @secure
     */
    kpiGroup: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<KPIGroup[]>>({
        url: `/KPIGroup`,
        method: 'GET',
        ...config
      })
  },
  survey: {
    /**
     *  Obtiene el all template
     *
     * @tags Survey
     * @name getTemplate
     * @request GET: /api/v1/Survey/templates/group
     * @secure
     */
    getAllTemplate: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Survey/templates/group`,
        method: 'GET',
        ...config
      }),
    /**
     *  Obtiene un template por id
     *
     * @tags Survey
     * @name getTemplateById
     * @request GET: /api/v1/Survey/TemplateById/{id}
     * @secure
     */
    getTemplateById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SurveyTemplatesResponse>>({
        url: `/Survey/TemplateById/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     *  Obtiene un template por TemplateByIdTicket
     *
     * @tags Survey
     * @name getTemplateByIdTicket
     * @request GET: /api/v1/Survey/TemplateByIdTicket/{id}
     * @secure
     */
    getTemplateByIdTicket: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SurveyTemplatesResponse>>({
        url: `/Survey/TemplateByIdTicket/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene el template de encuesta activo por categoría/subcategoría
     *
     * @tags Survey
     * @name getTemplateBySubCategory
     * @request GET: /api/v1/Survey/template/by-subcategory/{subCategoryId}
     * @secure
     */
    getTemplateBySubCategory: (
      subCategoryId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<SurveyTemplatesResponse>>({
        url: `/Survey/template/by-subcategory/${subCategoryId}`,
        method: 'GET',
        ...config
      }),
    /**
     *  Crea un template
     *
     * @tags Survey
     * @name createTemplate
     * @request POST: /api/v1/Survey/template
     * @secure
     */
    createTemplate: (data: ISurveyTemplate, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Survey/template`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     *  Contestar encuesta
     *
     * @tags Survey
     * @name SaveanswerSurvey
     * @request POST: /api/v1/Survey
     * @secure
     */
    SaveanswerSurvey: (
      data: SaveanswerSurvey,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Survey`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     *  Actualiza un template
     *
     * @tags Survey
     * @name updateTemplate
     * @request PUT: /api/v1/Survey/template/{id}
     * @secure
     */
    updateTemplate: (data: ISurveyTemplate, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<[]>>({
        url: `/Survey/template/${data?.idTemplate}`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     *  Obtiene todos los templates
     *
     * @tags Survey
     * @name getAllTemplates
     * @request GET: /api/v1/Survey/templates
     * @secure
     */
    getAllTemplates: (params: ParamsSurvey, config: AxiosRequestConfig = {}) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<SurveyTemplatesResponse>>
      >({
        url: `/Survey/templates`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     *  Obtiene todos los reportes paginados
     *
     * @tags Survey
     * @name getReports
     * @request GET: /api/v1/Survey/report
     * @secure
     */
    getReports: (params: ParamsSurvey, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PagedList<ISurveyReport>>>({
        url: `/Survey/report`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     *  Obtiene el detalle de un reporte
     *
     * @tags Survey
     * @name getReportById
     * @request GET: /api/v1/Survey/report/{id}
     * @secure
     */
    getReportById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ISurveyDetail[]>>({
        url: `/Survey/report/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     *  Obtiene todos los encuestas respondidas  paginados
     *
     * @tags Survey
     * @name getSurveysAnsweredByTemplate
     * @request GET: /api/v1/Survey/report/survey
     * @secure
     */
    getSurveysAnsweredByTemplate: (
      params: ParamsSurvey,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<ISurveyAsnwered>>>({
        url: `/Survey/report/survey`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Obtener todas las respuestas de una encuesta específica
     *
     * @tags Survey
     * @name getReportById
     * @request GET: /api/v1/Survey/report/survey/{id}/answers
     * @secure
     */
    getSurveyAnsweredById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<ISurveyAnsweredDetail[]>>({
        url: `/Survey/report/survey/${id}/answers`,
        method: 'GET',
        ...config
      }),
    /**
     * Elimina una encuesta específica
     *
     * @tags Survey
     * @name deleteSurveyById
     * @request DELETE: /api/v1/Survey/template/{id}
     * @secure
     */
    deleteSurveyById: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<void>>({
        url: `/Survey/template/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * Duplica una encuesta específica
     *
     * @tags Survey
     * @name duplicateSurveyById
     * @request POST: /api/v1/Survey/template/{id}/duplicate
     * @secure
     */
    duplicateSurveyById: (
      data: { templateId: number; title: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<void>>({
        url: `/Survey/template/duplicate`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Obtiene una encuesta específica por id de cliente en cambios
     *
     * @tags Survey
     * @name getSurveyByIdClientChange
     * @request GET: /api/v1/Survey/templates/change/105
     * @secure
     */
    getSurveyByIdClientChange: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SurveyTemplatesBychange>>({
        url: `/Survey/templates/change/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene una encuesta específica por id de cliente en Problemas
     *
     * @tags Survey
     * @name getSurveyByIdClientProblem
     * @request GET: /api/v1/Survey/templates/problem/105
     * @secure
     */
    getSurveyByIdClientProblem: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SurveyTemplatesBychange>>({
        url: `/Survey/templates/problem/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * Calcula la prioridad de un cambio
     *
     * @tags Survey
     * @name CalculateChangePriority
     * @request GET: /api/v1/Survey/CalculateChangePriority
     * @secure
     */
    CalculateChangePriority: (
      data: SaveanswerSurvey,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<CalculateChangePriority>>({
        url: `/Survey/CalculateChangePriority`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * Calcula la prioridad de un problema
     *
     * @tags Survey
     * @name CalculateProblemPriority
     * @request GET: /api/v1/Survey/CalculateProblemPriority
     * @secure
     */
    CalculateProblemPriority: (
      data: SaveanswerSurvey,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<void>>({
        url: `/Survey/CalculateProblemPriority`,
        method: 'POST',
        data,
        ...config
      })
  },
  kpiParameter: {
    /**
     * Obtiene el listado de los parametros
     *
     * @tags KPIParameter
     * @name kpiParameter
     * @request GET:/api/v1/KPIParameter
     * @secure
     */
    kpiParameter: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<KPIParameter[]>>({
        url: `/KPIParameter`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene los filtros según el parámetro
     * @tags KPIParameter
     * @name getFilters
     * @request GET:/api/v1/KPIParameter/filterdata/{ParameterFilterId}
     * @secure
     */
    getFilters: (params: KPIFilter, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/KPIParameter/filterdata/${params.parameterFilterId}`,
        method: 'GET',
        params,
        ...config
      })
  }
};
